import { Inject, Injectable } from '@angular/core';

import {
    Fixture,
    Participant,
    ParticipantMarketStatus,
    ParticipantMarketType,
    ParticipantOption,
    ParticipantPriceType,
    ParticipantType,
} from '@cds/betting-offer';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { emptyOdds } from '@frontend/sports/odds/feature';

import { HorsesFixtureHelper } from '../../../../lib/horse-racing-shared/services/horses-fixture-helper.service';
import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { formatObject } from '../../../base/utils/format-object';
import { BetslipV2ParticipantPick } from '../../../core/picks/betslip-v2-participant-pick';
import { BetslipV2WinParticipantPick } from '../../../core/picks/betslip-v2-win-participant-pick';
import { V2ParticipantPickId, V2WinPickId } from '../../../core/picks/pick-id';
import {
    HorseRaceParticipantPickType,
    IBetslipV2PickParticipant,
    IBetslipV2PickWinParticipantMarket,
    IPrice,
    ParticipantPickType,
    PriceType,
} from '../../../core/picks/pick-models';
import { createSignedName } from '../../../core/picks/signed-name.model';
import { BetslipV2GolfWinParticipantPick } from '../../../core/picks/sport-specific/betslip-v2-golf-picks';
import { BetslipV2HorseRaceWinParticipantPick, BetslipV2HorseRaceXCastPick } from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { participantOptionPriceToBetslipPrice } from '../../../core/price';
import AlternativeTranslationsService from '../../../integration/services/alternative-translations-impl.service';
import V2PickFactory from './v2-pick.factory';

type ParticipantsFixture = Omit<Fixture, 'optionMarkets' | 'games'>;

@Injectable()
export default class ParticipantPickFactory extends V2PickFactory {
    private messages: {
        racingBetTypes: {
            betTypeWin: string;
            betTypeWinAlt: string;
            betTypeForecast: string;
            betTypeForecastAlt: string;
            betTypeTricast: string;
            betTypeTricastAlt: string;
            betTypeCombinationForecast: string;
            betTypeCombinationForecastAlt: string;
            betTypeCombinationTricast: string;
            betTypeCombinationTricastAlt: string;
        };
        racingMarketNames: {
            combinationForecast: string;
            combinationForecastAlt: string;
            combinationTricast: string;
            combinationTricastAlt: string;
            forecast: string;
            forecastAlt: string;
            tricast: string;
            tricastAlt: string;
            win: string;
            winAlt: string;
        };
    };

    constructor(
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private alternativeTranslation: AlternativeTranslationsService,
        private horsesFixtureHelper: HorsesFixtureHelper,
    ) {
        super();
        const { racingBetTypes, racingMarketNames } = messageProvider.getMessages();
        this.messages = { racingBetTypes, racingMarketNames };
    }

    createWinPick(pickId: V2WinPickId, fixture: ParticipantsFixture, priceId?: number): BetslipV2ParticipantPick | null {
        if (!this.validateFixtureForParticipantPick(pickId, fixture)) {
            return null;
        }
        switch (fixture.sport.id) {
            case SportConstant.Horses:
            case SportConstant.Greyhounds: {
                const pick = new BetslipV2HorseRaceWinParticipantPick();
                pick.id = pickId;
                if (fixture.sport.id === SportConstant.Horses) {
                    fixture.addons.bestOddsGuarantee = this.horsesFixtureHelper.checkIfBOGApplicable(fixture);
                }
                pick.fixture = this.mapHorseFixture(fixture);
                this.mapWinParticipantPick<IBetslipV2PickParticipant>(
                    pick,
                    fixture,
                    (p) => ({
                        drawNumber: p.properties?.drawNumber || 0,
                    }),
                    priceId,
                );
                this.setHorseRaceParticipantPickMarketName(pick);

                return pick;
            }
            case SportConstant.Golf: {
                const pick = new BetslipV2GolfWinParticipantPick();
                pick.id = pickId;
                if (!fixture.fixtureGroup) {
                    return null;
                }

                pick.fixture = {
                    ...this.mapFixture(fixture),
                    fixtureGroup: {
                        id: fixture.fixtureGroup.id,
                        name: createSignedName(fixture.fixtureGroup.name),
                    },
                };
                this.mapWinParticipantPick(pick, fixture);

                return pick;
            }
            default: {
                throw new Error('Not supported sport for win pick');
            }
        }
    }

    createXCastPick(pickId: V2ParticipantPickId, fixture: ParticipantsFixture): BetslipV2ParticipantPick | null {
        if (!this.validateFixtureForParticipantPick(pickId, fixture)) {
            return null;
        }
        const pick = new BetslipV2HorseRaceXCastPick();
        pick.id = pickId;
        pick.fixture = this.mapHorseFixture(fixture);

        const options = fixture.participants.flatMap((p) => p.options);
        const marketType =
            pickId.pickType === ParticipantPickType.Forecast || pickId.pickType === ParticipantPickType.CombinationForecast
                ? ParticipantMarketType.Forecast
                : ParticipantMarketType.Tricast;
        const market = options.find((o) => o.marketType === marketType)!;

        pick.market = {
            id: market.marketId,
            marketType: market.marketType,
            isVisible: market.marketStatus === ParticipantMarketStatus.Visible,
            isBetBuilderEnabled: false,
        };

        pick.participants = fixture.participants
            .filter((p) => p.id! in pickId.participantIds)
            .map((p) => ({
                ...this.mapParticipant(p, pick.market.id),
                position: pickId.participantIds[p.id!],
                drawNumber: p.properties?.drawNumber || 0,
            }));

        pick.acceptedPrice = {
            isManuallyAccepted: true,
            price: {
                nativeOdds: emptyOdds,
                type: PriceType.NoPrice,
                id: -1,
                marketId: market.id,
                isVisible: true,
            },
        };

        this.setParticipantPickBetType(pick);
        this.setParticipantPickBetTypeAltTranslation(pick);
        this.setHorseRaceParticipantPickMarketName(pick);

        return pick;
    }

    private validateFixtureForParticipantPick(pickId: V2ParticipantPickId, fixture: ParticipantsFixture): boolean {
        const participantIds = Object.keys(pickId.participantIds).map((id) => +id);
        const participants = fixture.participants.filter((p) => participantIds.some((pId) => p.id === pId));
        if (participantIds.length !== participants.length) {
            return false;
        }
        if (participants.some((p) => p.options.length === 0)) {
            // All participants should have options.
            return false;
        }
        switch (pickId.pickType) {
            case ParticipantPickType.Win: {
                if (participants.length !== 1) {
                    return false;
                }
                const participant = participants[0];
                if (!participant) {
                    // the participant from the pick id should exists
                    return false;
                }
                const markets = participant.options.filter((o) => o.marketType === ParticipantMarketType.Win);
                if (markets.length === 0) {
                    // should have at least fixed or starting price option.
                    return false;
                }

                return true;
            }
            case ParticipantPickType.Forecast:
            case ParticipantPickType.CombinationForecast: {
                if (participants.length < 2) {
                    return false;
                }
                const hasForecastMarket = participants.every((p) => p.options.some((o) => o.marketType === ParticipantMarketType.Forecast));
                if (!hasForecastMarket) {
                    return false;
                }

                return true;
            }
            case ParticipantPickType.Tricast:
            case ParticipantPickType.CombinationTricast: {
                if (participants.length < 3) {
                    return false;
                }
                const hasTricastMarket = participants.every((p) => p.options.some((o) => o.marketType === ParticipantMarketType.Tricast));
                if (!hasTricastMarket) {
                    return false;
                }

                return true;
            }
            case ParticipantPickType.Freeform:
                return true;
        }

        return false;
    }

    private mapWinMarket(winOptions: ParticipantOption[]): IBetslipV2PickWinParticipantMarket {
        const fixedPrice = winOptions.find((o) => o.priceType === ParticipantPriceType.Fixed);
        const spPrice = winOptions.find((o) => o.priceType === ParticipantPriceType.StartingPrice);

        return {
            isEachWay: !!fixedPrice?.properties.isEachWay,
            marketType: ParticipantMarketType.Win,
            placeTerms:
                (fixedPrice?.properties.placeTerms && {
                    ...fixedPrice.properties.placeTerms,
                }) ||
                null,
            id: fixedPrice ? fixedPrice.marketId : spPrice!.marketId, // we check that there is at least one fixed or SP prices in validate data method.
            isVisible: !!(fixedPrice && fixedPrice.marketStatus === ParticipantMarketStatus.Visible),
            isStartingPriceAvailable: !!(spPrice && spPrice.marketStatus === ParticipantMarketStatus.Visible),
            isBetBuilderEnabled: false,
        };
    }

    private mapParticipant(participant: Participant, marketId: number): IBetslipV2PickParticipant {
        return {
            fixtureParticipantId: participant.id!,
            participantId: participant.participantId,
            type: participant.properties?.type || ParticipantType.Unknown,
            name: createSignedName(participant.name),
            status: participant.status!,
            position: 0,
            prices: participant.options.filter((o) => o.marketId === marketId).map((price) => participantOptionPriceToBetslipPrice(price)),
        };
    }

    private getWinPickPriceType(market: IBetslipV2PickWinParticipantMarket, prices: IPrice[], priceId?: number): PriceType {
        if (priceId) {
            return prices.find((pr) => pr.id === priceId)!.type;
        }
        if (market.isVisible && prices.some((pr) => pr.isVisible && pr.type === PriceType.Fixed)) {
            return PriceType.Fixed;
        }

        return PriceType.StartingPrice;
    }

    private mapWinParticipantPick<TParticipant extends IBetslipV2PickParticipant>(
        pick: BetslipV2WinParticipantPick,
        fixture: ParticipantsFixture,
        participantExtraProps: (participant: Participant) => Partial<TParticipant> = () => ({}) as Partial<TParticipant>,
        priceId?: number,
    ): void {
        const participant = fixture.participants.find((p) => p.id === pick.id.participantId)!;
        const options = participant.options.filter((o) => o.marketType === ParticipantMarketType.Win);
        pick.market = this.mapWinMarket(options);

        pick.participants = [
            {
                ...this.mapParticipant(participant, pick.market.id),
                ...(participantExtraProps(participant) as any),
            },
        ] as TParticipant[];

        pick.participants.forEach((pickParticipant, index) => {
            pickParticipant.position = index + 1;
        });

        pick.priceType = this.getWinPickPriceType(pick.market, pick.prices, priceId);
        pick.acceptedPrice = {
            price: pick.currentPrice ? { ...pick.currentPrice } : null,
            isManuallyAccepted: false,
        };
        this.setParticipantPickBetType(pick);
        this.setParticipantPickBetTypeAltTranslation(pick);
    }

    private setParticipantPickBetType(pick: BetslipV2ParticipantPick): void {
        switch (pick.id.pickType) {
            case ParticipantPickType.CombinationForecast:
                pick.betType = formatObject(this.messages.racingBetTypes.betTypeCombinationForecast, [pick.betCount()]);
                break;
            case ParticipantPickType.CombinationTricast:
                pick.betType = formatObject(this.messages.racingBetTypes.betTypeCombinationTricast, [pick.betCount()]);
                break;
            case ParticipantPickType.Forecast:
                pick.betType = this.messages.racingBetTypes.betTypeForecast;
                break;
            case ParticipantPickType.Tricast:
                pick.betType = this.messages.racingBetTypes.betTypeTricast;
                break;
            case ParticipantPickType.Win:
                pick.betType = this.messages.racingBetTypes.betTypeWin;
                break;
        }
    }

    /**
     * Set alt bet type. In betstation for the receipt only english should be used,
     * Alt messages are only in English while its sibling is in user language.
     *
     * @param pick
     * @private
     */
    private setParticipantPickBetTypeAltTranslation(pick: BetslipV2ParticipantPick): void {
        if (!this.alternativeTranslation.useBaseTranslations() || !this.messages.racingBetTypes.betTypeWinAlt) {
            return;
        }

        switch (pick.id.pickType) {
            case ParticipantPickType.CombinationForecast:
                pick.betTypeAltTranslation = formatObject(this.messages.racingBetTypes.betTypeCombinationForecastAlt, [pick.betCount()]);
                break;
            case ParticipantPickType.CombinationTricast:
                pick.betTypeAltTranslation = formatObject(this.messages.racingBetTypes.betTypeCombinationTricastAlt, [pick.betCount()]);
                break;
            case ParticipantPickType.Forecast:
                pick.betTypeAltTranslation = this.messages.racingBetTypes.betTypeForecastAlt;
                break;
            case ParticipantPickType.Tricast:
                pick.betTypeAltTranslation = this.messages.racingBetTypes.betTypeTricastAlt;
                break;
            case ParticipantPickType.Win:
                pick.betTypeAltTranslation = this.messages.racingBetTypes.betTypeWinAlt;
                break;
        }
    }

    private setHorseRaceParticipantPickMarketName(pick: HorseRaceParticipantPickType): void {
        switch (pick.id.pickType) {
            case ParticipantPickType.CombinationForecast:
                pick.setMarketName(
                    `${pick.betCount()}x ${this.messages.racingMarketNames.combinationForecast}`,
                    this.getRacingMarketAltTranslation(pick),
                );
                break;
            case ParticipantPickType.CombinationTricast:
                pick.setMarketName(
                    `${pick.betCount()}x ${this.messages.racingMarketNames.combinationTricast}`,
                    this.getRacingMarketAltTranslation(pick),
                );
                break;
            case ParticipantPickType.Forecast:
                pick.setMarketName(this.messages.racingMarketNames.forecast, this.getRacingMarketAltTranslation(pick));
                break;
            case ParticipantPickType.Tricast:
                pick.setMarketName(this.messages.racingMarketNames.tricast, this.getRacingMarketAltTranslation(pick));
                break;
            case ParticipantPickType.Win:
                pick.setMarketName(this.messages.racingMarketNames.win, this.getRacingMarketAltTranslation(pick));
                break;
        }
    }

    private getRacingMarketAltTranslation(pick: HorseRaceParticipantPickType): string | undefined {
        if (!this.alternativeTranslation.useBaseTranslations() || !this.messages.racingMarketNames.winAlt) {
            return;
        }

        switch (pick.id.pickType) {
            case ParticipantPickType.CombinationForecast:
                return `${pick.betCount()}x ${this.messages.racingMarketNames.combinationForecastAlt}`;
            case ParticipantPickType.CombinationTricast:
                return `${pick.betCount()}x ${this.messages.racingMarketNames.combinationTricastAlt}`;
            case ParticipantPickType.Forecast:
                return this.messages.racingMarketNames.forecastAlt;
            case ParticipantPickType.Tricast:
                return this.messages.racingMarketNames.tricastAlt;
            case ParticipantPickType.Win:
                return this.messages.racingMarketNames.winAlt;
        }

        return;
    }
}
